import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { LockKeyhole, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { Header } from '@/components/layout/Header'

export function LoginPage() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { login, user, loading } = useAuth()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'robots'
      document.head.appendChild(meta)
    }
    meta.content = 'noindex, nofollow'
    return () => {
      meta?.remove()
    }
  }, [])

  useEffect(() => {
    if (!loading && user) {
      navigate('/admin/dashboard', { replace: true })
    }
  }, [user, loading, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!username.trim() || !password.trim()) {
      setError(t('login.error.required', 'Inserisci username e password'))
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      await login(username, password)
      navigate('/admin/dashboard')
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('login.error.invalidCredentials', 'Credenziali non valide'),
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Header />
      <section className="flex min-h-screen items-center justify-center bg-ink-deep px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
        <div className="text-center">
          <h1 className="font-display text-4xl font-bold text-white md:text-5xl">
            {t('login.title', 'Area Riservata')}
          </h1>
          <p className="mx-auto mt-4 max-w-sm text-base text-white/70 md:text-lg">
            {t('login.subtitle', 'Accedi per gestire contenuti e prenotazioni')}
          </p>
        </div>

        <div className="mt-10 rounded-3xl border border-primary/20 bg-ink p-8 shadow-2xl md:p-10">
          <div className="mb-6 flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <LockKeyhole className="h-6 w-6" aria-hidden="true" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div>
              <label
                htmlFor="admin-username"
                className="mb-2 block text-xs font-semibold uppercase tracking-widest text-white/50"
              >
                {t('login.username', 'Username')}
              </label>
              <input
                id="admin-username"
                type="text"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value)
                  if (error) setError('')
                }}
                disabled={isSubmitting}
                className={cn(
                  'block w-full rounded-xl border bg-cream/5 px-4 py-3 text-white placeholder:text-white/30 transition-colors',
                  'focus:border-primary focus:bg-cream/10 focus:outline-none focus:ring-2 focus:ring-primary/20',
                  'disabled:opacity-50',
                  error ? 'border-red-400/50' : 'border-white/10',
                )}
                placeholder={t('login.username', 'Username')}
              />
            </div>

            <div>
              <label
                htmlFor="admin-password"
                className="mb-2 block text-xs font-semibold uppercase tracking-widest text-white/50"
              >
                {t('login.password', 'Password')}
              </label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  if (error) setError('')
                }}
                disabled={isSubmitting}
                className={cn(
                  'block w-full rounded-xl border bg-cream/5 px-4 py-3 text-white placeholder:text-white/30 transition-colors',
                  'focus:border-primary focus:bg-cream/10 focus:outline-none focus:ring-2 focus:ring-primary/20',
                  'disabled:opacity-50',
                  error ? 'border-red-400/50' : 'border-white/10',
                )}
                placeholder={t('login.password', 'Password')}
              />
            </div>

            {error && (
              <p className="text-sm font-medium text-red-400" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={cn(
                'w-full rounded-full bg-primary px-8 py-4 text-sm font-bold text-ink shadow-lg transition-colors',
                'hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-70',
              )}
            >
              {isSubmitting ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('login.submitting', 'Accesso...')}
                </span>
              ) : (
                t('login.submit', 'Accedi')
              )}
            </button>
          </form>
        </div>
        </div>
      </section>
    </>
  )
}
